import React, { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { getData, patchData, postData } from "../../services/request"
import CvScreeningMessageComposer from "./CvScreeningMessageComposer"
import {
  apiErrorMessage,
  formatScore,
  itemStatusLabel,
  recommendationClass,
  recommendationLabel,
  scoreBarColor,
} from "./cvScreeningUtils"
const CvScreeningItemPage = () => {
  const { batchId, itemId } = useParams()
  const [item, setItem] = useState(null)
  const [batchMeta, setBatchMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [emailDraft, setEmailDraft] = useState("")
  const [savingEmail, setSavingEmail] = useState(false)
  const [emailNotice, setEmailNotice] = useState("")
  const [composerOpen, setComposerOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendNotice, setSendNotice] = useState("")

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError("")
      const url = `${getAPIBaseURL()}/employme/employer/cv-screening/${batchId}/items/${itemId}`
      const res = await getData(url, {}, {})
      if (cancelled) return
      setLoading(false)
      if (res.error === "" && res.result?.status === 200) {
        const data = res.result.data?.data || {}
        setItem(data.item || null)
        setBatchMeta(data.batch || null)
        setEmailDraft(data.item?.candidateEmail || "")
      } else {
        setError(apiErrorMessage(res, "Could not load CV report"))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [batchId, itemId])

  const saveEmail = async () => {
    setSavingEmail(true)
    setError("")
    setEmailNotice("")
    const url = `${getAPIBaseURL()}/employme/employer/cv-screening/${batchId}/items/${itemId}`
    const res = await patchData(
      url,
      { "Content-Type": "application/json" },
      { candidateEmail: emailDraft.trim() }
    )
    setSavingEmail(false)
    if (res.error === "" && res.result?.status === 200) {
      const updated = res.result.data?.data || {}
      setItem((prev) => ({ ...prev, ...updated }))
      setEmailDraft(updated.candidateEmail || emailDraft.trim())
      setEmailNotice(updated.candidateEmail ? "Email saved" : "Email cleared")
    } else {
      setError(apiErrorMessage(res, "Could not save email"))
    }
  }

  const handleSend = async ({ subject, body }) => {
    setSending(true)
    setError("")
    setSendNotice("")
    const draft = emailDraft.trim()
    if (draft && draft !== (item?.candidateEmail || "")) {
      const saveUrl = `${getAPIBaseURL()}/employme/employer/cv-screening/${batchId}/items/${itemId}`
      const saveRes = await patchData(
        saveUrl,
        { "Content-Type": "application/json" },
        { candidateEmail: draft }
      )
      if (saveRes.error !== "" || saveRes.result?.status !== 200) {
        setSending(false)
        setError(apiErrorMessage(saveRes, "Save the candidate email before sending"))
        return
      }
      const updated = saveRes.result.data?.data || {}
      setItem((prev) => ({ ...prev, ...updated }))
      setEmailDraft(updated.candidateEmail || draft)
    }
    const url = `${getAPIBaseURL()}/employme/employer/cv-screening/${batchId}/messages`
    const res = await postData(
      url,
      { "Content-Type": "application/json" },
      JSON.stringify({
        audience: "INDIVIDUAL",
        itemIds: [itemId],
        subject,
        body,
      })
    )
    setSending(false)
    if (res.error === "" && (res.result?.status === 200 || res.result?.status === 201)) {
      setSendNotice(res.result.data?.data?.message || "Email sent")
      setComposerOpen(false)
    } else {
      setError(apiErrorMessage(res, "Could not send email"))
    }
  }

  const fileUrl = `${getAPIBaseURL()}/employme/employer/cv-screening/${batchId}/items/${itemId}/file`
  const metricLabel = (key) =>
    (batchMeta?.metrics || []).find((m) => m.key === key)?.label || key

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl rounded-xl border border-gray-200 bg-white p-10 text-center">
        <div className="mx-auto mb-3 h-8 w-8 animate-pulse-soft rounded-full bg-slate-200" />
        <p className="text-sm text-gray-500">Loading report...</p>
      </div>
    )
  }

  if (!item) {
    return (
      <div className="mx-auto max-w-3xl">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error || "Report not found"}
        </div>
        <Link
          to={`/employer/cv-screening/${batchId}`}
          className="mt-4 inline-block text-sm text-cyan-700"
        >
          ← Back to batch
        </Link>
      </div>
    )
  }

  const score = item.overallScore

  return (
    <div className="mx-auto max-w-3xl animate-fade-in">
      <div className="mb-4">
        <Link
          to={`/employer/cv-screening/${batchId}`}
          className="text-xs font-medium text-cyan-700 hover:text-cyan-900"
        >
          ← Back to batch
        </Link>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 px-5 py-8 text-white sm:px-8">
          <div
            className="pointer-events-none absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "radial-gradient(circle at 80% 20%, rgba(0,165,230,0.45), transparent 45%)",
            }}
          />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200/90">
                CV report
              </p>
              <h2 className="mt-2 truncate text-2xl font-semibold tracking-tight">
                {item.candidateName || item.originalName || "Candidate"}
              </h2>
              <p className="mt-1 truncate text-sm text-slate-300">
                {batchMeta?.roleSnapshot?.title || "Role"} · {item.originalName}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {item.recommendation ? (
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${recommendationClass(
                      item.recommendation
                    )}`}
                  >
                    {recommendationLabel(item.recommendation)}
                  </span>
                ) : (
                  <span className="rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-semibold text-white">
                    {itemStatusLabel(item.status)}
                  </span>
                )}
              </div>
            </div>
            <div className="shrink-0 text-center sm:text-right">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-cyan-100/80">
                Overall score
              </p>
              <p className="mt-1 text-5xl font-semibold tabular-nums tracking-tight">
                {formatScore(score)}
              </p>
            </div>
          </div>
          {typeof score === "number" ? (
            <div className="relative mt-5 h-2 overflow-hidden rounded-full bg-white/15">
              <div
                className={`h-full rounded-full transition-all duration-700 ${scoreBarColor(score)}`}
                style={{ width: `${Math.min(100, score)}%` }}
              />
            </div>
          ) : null}
        </div>

        <div className="space-y-5 p-5 sm:p-8">
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}
          {sendNotice || emailNotice ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {sendNotice || emailNotice}
            </div>
          ) : null}

          <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
            <h3 className="text-sm font-semibold text-gray-900">Candidate email</h3>
            <p className="mt-1 text-xs text-gray-500">
              Pulled from the CV when possible. Add or correct it before sending a message.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                placeholder="candidate@email.com"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={saveEmail}
                disabled={savingEmail}
                className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-white disabled:opacity-50"
              >
                {savingEmail ? "Saving..." : "Save email"}
              </button>
            </div>
          </section>

          {item.error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {item.error}
            </div>
          ) : null}
          {item.summary ? (
            <section>
              <h3 className="text-sm font-semibold text-gray-900">Summary</h3>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-700">
                {item.summary}
              </p>
            </section>
          ) : null}

          {(item.scores || []).length ? (
            <section>
              <h3 className="text-sm font-semibold text-gray-900">Metric scores</h3>
              <ul className="mt-3 space-y-4">
                {item.scores.map((s, idx) => (
                  <li
                    key={`${s.metricKey}-${idx}`}
                    className="rounded-xl border border-gray-100 bg-slate-50/80 p-4 animate-slide-up"
                    style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
                  >
                    <div className="mb-2 flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold text-gray-900">
                        {metricLabel(s.metricKey)}
                      </p>
                      <span className="text-sm font-semibold tabular-nums text-gray-900">
                        {formatScore(s.score)}
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-white">
                      <div
                        className={`h-full rounded-full ${scoreBarColor(s.score)}`}
                        style={{
                          width: `${typeof s.score === "number" ? Math.min(100, s.score) : 0}%`,
                        }}
                      />
                    </div>
                    {s.rationale ? (
                      <p className="mt-2 text-xs leading-relaxed text-gray-600">{s.rationale}</p>
                    ) : null}
                    {(s.evidence || []).length ? (
                      <ul className="mt-2 space-y-1">
                        {s.evidence.map((ev, i) => (
                          <li
                            key={i}
                            className="border-l-2 border-cyan-300 pl-2 text-[11px] italic text-gray-500"
                          >
                            “{ev}”
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <section className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
              <h3 className="text-sm font-semibold text-emerald-900">Strengths</h3>
              {(item.strengths || []).length ? (
                <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-emerald-900/80">
                  {item.strengths.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-emerald-800/60">None listed</p>
              )}
            </section>
            <section className="rounded-xl border border-amber-100 bg-amber-50/50 p-4">
              <h3 className="text-sm font-semibold text-amber-900">Gaps</h3>
              {(item.gaps || []).length ? (
                <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-amber-900/80">
                  {item.gaps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-amber-800/60">None listed</p>
              )}
            </section>
          </div>

          <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
            {item.status === "DONE" ? (
              <button
                type="button"
                onClick={() => {
                  setError("")
                  setSendNotice("")
                  setComposerOpen(true)
                }}
                className="inline-flex rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900"
              >
                Email this candidate
              </button>
            ) : null}
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Open original CV
            </a>
            <Link
              to={`/employer/cv-screening/${batchId}`}
              className="inline-flex rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Back to batch
            </Link>
          </div>
        </div>
      </div>

      <CvScreeningMessageComposer
        open={composerOpen}
        onClose={() => !sending && setComposerOpen(false)}
        audience="INDIVIDUAL"
        candidateName={item.candidateName || item.originalName}
        roleTitle={batchMeta?.roleSnapshot?.title}
        sending={sending}
        error={composerOpen ? error : ""}
        onSend={handleSend}
      />
    </div>
  )
}

export default CvScreeningItemPage
